import { useField } from 'formik'
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react'
import { Form, Input } from 'semantic-ui-react'
import FWOLabel from '../form/FWOLabel'
import debounce from 'lodash/debounce'
import { ISearch } from '../../models/Common'
import './FWOAutoComplete.css'

export interface IAutoCompleteProps {
  name: string
  label: string
  placeholder?: string
  required?: boolean
  tooltip?: string
  title?: string
  showreadonly?: boolean
  visible?: boolean
  loading?: boolean
  suggestions?: any
  noSuggestions?: string
  userErrors?: any
  hasError?: (any) => void
  requests?: (any) => void
  renderSuggestion: (suggestion: any, index: number) => void
  clearNoSuggestionOrError: () => void
}

const FWOAutoComplete = forwardRef((props: IAutoCompleteProps, ref) => {
  const wrapperRef = useRef(null)
  const [parentField, parentMeta, parentHelpers] = useField(props.name)
  const [search, setSearch] = useState<ISearch>({ term: parentField.value })
  const [selected, setSelected] = useState<string>(parentField.value || null)
  const [current, setCurrent] = useState<string>(parentField.value || '')
  const [changed, setChanged] = useState<boolean>(false)
  const [display, setDisplay] = useState(false)

  useImperativeHandle(ref, () => ({
    setSelection(selection: string) {
      setSearch((prevState) => ({
        ...prevState,
        term: selection,
      }))
      if (selection) {
        parentHelpers.setValue(selection)
      }
      setDisplay(false)
      setSelected(selection)
    },
  }))

  const debouncedSave = useCallback(
    debounce((newValue) => props.requests(newValue), 500),
    []
  )

  const updateValue = (keyword: string) => {
    setSearch((prevState) => ({
      ...prevState,
      term: keyword,
    }))
    debouncedSave(keyword)
    setDisplay(true)
  }

  useEffect(() => {
    window.addEventListener('mousedown', handleClickOutside)
    return () => {
      window.removeEventListener('mousedown', handleClickOutside)
    }
  })

  const handleClickOutside = (event) => {
    const { current: wrap } = wrapperRef
    if (wrap && !wrap.contains(event.target)) {
      setDisplay(false)
    }
    if (selected === null) {
      parentHelpers.setValue('')
      setSearch((prevState) => ({
        ...prevState,
        term: '',
      }))
    }
    props.clearNoSuggestionOrError()
  }

  return (
    <>
      <div ref={wrapperRef}>
        <Form.Field
          autoComplete="new-password"
          error={
            parentMeta.touched &&
            parentField.value != null &&
            !!parentMeta.error
          }
          {...{ required: props.required }}
        >
          <div className="sixteen wide field" id={props.name + 'AutoComplete'}>
            <FWOLabel
              label={props.label}
              name={props.name}
              tooltip={props.tooltip}
              required={props.required}
            />
            {parentMeta.touched &&
            parentField.value != null &&
            parentMeta.error ? (
              <span className="validation-error">{parentMeta.error}</span>
            ) : null}
            <Input
              autoComplete="new-password"
              {...{ loading: props.loading }}
              {...parentField}
              {...{
                id: props.name,
                name: props.name,
                type: 'text',
                placeholder: `${
                  props.placeholder == null ? '' : props.placeholder
                }`,
              }}
              title={`${props.tooltip == null ? props.title : props.tooltip}`}
              {...{ readOnly: props.showreadonly == true }}
              value={search.term}
              onFocus={() => {
                if (props.showreadonly) {
                  return
                }
                setChanged(true)
                parentHelpers.setValue('')
                setSearch({ term: '' })
                setCurrent(parentField.value)
              }}
              onClick={() => {
                setDisplay(!display)
              }}
              onChange={(input) => {
                setChanged(true)
                updateValue(input.target.value)
              }}
              onBlur={() => {
                if (changed) {
                  parentHelpers.setValue(current)
                  setSearch({ term: current })
                }
                props.clearNoSuggestionOrError()
              }}
              onKeyPress={(e) => {
                e.which === 13 && e.preventDefault()
              }}
            />
            {display && (
              <div
                className={`auto-complete-results ${
                  props.suggestions !== null && props.suggestions.length > 0
                    ? 'scroll'
                    : ''
                }`}
              >
                {props.suggestions?.map((suggestion, index) => {
                  {
                    return props.renderSuggestion(suggestion, index)
                  }
                })}
              </div>
            )}
            {display && props.noSuggestions?.length > 0 && (
              <div className="auto-complete-results has-no-results">
                {props.noSuggestions}
              </div>
            )}
            {display && props.userErrors?.length > 0 && (
              <div className="auto-complete-results has-errors">
                {props.userErrors}
              </div>
            )}
          </div>
        </Form.Field>
      </div>
    </>
  )
})
FWOAutoComplete.displayName = 'FWOAutoComplete'
export default FWOAutoComplete
